/**
 * レポートデータ更新（AnalysisData）用の純関数ヘルパー。 GenericStatusManagement / period_type /
 * 財務行の表示・進捗計算（SR-004,005,008,009,011,017,018）。
 */

import { format as formatDateFns, isValid, parse, parseISO } from 'date-fns'

import { formatCommaDigit } from '../../../../common/utils/NumberUtils'

import {
  ACCOUNTING_TARGET_STANDALONE_LABEL,
  GENERIC_DATA_KIND_GENERIC_ITEM,
  GENERIC_UPLOAD_FILE_STATUS_PENDING_DELETION,
  normalizeAccountingTargetForDisplay,
  PERIOD_TYPE_MONTHLY,
  PERIOD_TYPE_QUARTER,
  PERIOD_TYPE_YEAR
} from './types'

/**
 * 全体進捗の分子に含める GenericStatusManagement.Status 値（§6.1 ⑤）
 *
 * @readonly
 */
const PROGRESS_NUMERATOR_STATUSES = new Set(['imported', 'import-pending'])

/**
 * IF02095 画面表示（DB Status → 表示ラベル・色）。DB キーの別名は statusBadgeProps で正規化する。
 *
 * @type {Record<string, { label: string; badgeColor: string }>}
 * @readonly
 */
const GENERIC_STATUS_BADGE_MAP = {
  unset: { label: '未設定', badgeColor: 'gray' },
  configuring: { label: '設定中', badgeColor: 'orange' },
  'verification-pending': { label: '検証待ち', badgeColor: 'violet' },
  verifying: { label: '検証中', badgeColor: 'mint' },
  'verification-error': { label: '検証エラー', badgeColor: 'red' },
  'import-pending': { label: '更新待ち', badgeColor: 'blue' },
  'reimport-pending': { label: '再更新待ち', badgeColor: 'violet' },
  imported: { label: '更新済', badgeColor: 'green' }
}

/** API／DB で別表記のステータスキーをマップ先キーへ寄せる */
const GENERIC_STATUS_KEY_ALIAS = {
  'pending-verification': 'verification-pending'
}

/**
 * レスポンスの period_type をヘッダ種別表示に変換する（SR-004）
 *
 * @param {string | null | undefined} periodType
 * @returns {string}
 */
export const formatPeriodTypeLabel = (periodType) => {
  if (periodType == null || periodType === '') {
    return ''
  }
  switch (periodType) {
    case PERIOD_TYPE_MONTHLY:
      return '月次'
    case PERIOD_TYPE_QUARTER:
      return '四半期'
    case PERIOD_TYPE_YEAR:
      return '年次'
    default:
      return String(periodType)
  }
}

/**
 * 四半期/年次タスクで、ファイル無しでもデータ更新を許可してよいかを判定する（バグ#3136）。
 *
 * 対象期間の最終月にあたる月次タスクが更新済以上（IF02095 の closestMonthlyDataCreated=true）で、 かつ有効なアップロードファイル（pending-deletion を除く）が 0 件のときに
 * true を返す。 ファイルが 1 件でもある場合は通常フロー（全ファイルのアップロード完了が必要）に委ねるため false。 月次タスク、または月次が未更新のときも false。
 *
 * @param {string | null | undefined} periodType
 * @param {boolean | null | undefined} closestMonthlyDataCreated
 * @param {import('./types').UploadFileListItem[] | null | undefined} uploadFileList
 * @returns {boolean}
 */
export const isFileLessUpdateAllowed = (periodType, closestMonthlyDataCreated, uploadFileList) => {
  const isQuarterOrYear = periodType === PERIOD_TYPE_QUARTER || periodType === PERIOD_TYPE_YEAR
  if (!isQuarterOrYear || closestMonthlyDataCreated !== true) {
    return false
  }
  const activeCount = (uploadFileList ?? []).filter(
    (row) => row?.status !== GENERIC_UPLOAD_FILE_STATUS_PENDING_DELETION
  ).length
  return activeCount === 0
}

/**
 * 更新対象データ（期間）の表示用文字列（SR-005）。API の period_label をそのまま返す。
 *
 * @param {string | null | undefined} periodLabel
 * @returns {string}
 */
export const formatPeriodLabelForHeader = (periodLabel) => {
  if (periodLabel == null) {
    return ''
  }
  return String(periodLabel).trim()
}

/**
 * IF02095 の日時表示（upload_file_list / 財務 registered_at 等）を yyyy/MM/dd HH:mm に揃える
 *
 * @param {string | number | Date | null | undefined} value
 * @returns {string}
 */
export const formatTaskScreenDateTime = (value) => {
  const d = parseDateTimeValue(value)
  if (!d) {
    return value == null ? '' : String(value)
  }
  return formatDateFns(d, 'yyyy/MM/dd HH:mm')
}

/**
 * アップロード済みファイル一覧の日時（upload_file_list.update_date_time）を yyyy/MM/dd HH:mm:ss で表示する
 *
 * @param {string | number | Date | null | undefined} value
 * @returns {string}
 */
export const formatUploadFileListDateTime = (value) => {
  const d = parseDateTimeValue(value)
  if (!d) {
    return value == null ? '' : String(value)
  }
  return formatDateFns(d, 'yyyy/MM/dd HH:mm:ss')
}

/**
 * Upload_file_list の file_size / row_count 列（SR-099）。未設定・非数は「—」。
 *
 * @param {unknown} n
 * @returns {string}
 */
export const formatUploadFileListNumericCell = (n) => {
  if (n === null || n === undefined || n === '') {
    return '—'
  }
  const num = Number(n)
  if (Number.isNaN(num)) {
    return '—'
  }
  return formatCommaDigit(num)
}

/**
 * Upload_file_list の last_update_user_name 列。空は「—」。
 *
 * @param {unknown} s
 * @returns {string}
 */
export const formatUploadFileListUserCell = (s) => {
  const t = s == null ? '' : String(s).trim()
  return t === '' ? '—' : t
}

/**
 * Date/time value normalization for AnalysisData datetime rendering.
 *
 * @param {string | number | Date | null | undefined} value
 * @returns {Date | null}
 */
const parseDateTimeValue = (value) => {
  if (value == null || value === '') {
    return null
  }
  if (value instanceof Date) {
    return isValid(value) ? value : null
  }
  if (typeof value === 'string') {
    const text = value.trim()
    const isoParsed = parseISO(text)
    if (isValid(isoParsed)) {
      return isoParsed
    }
    // Some API values may contain slash-based date with ISO-like time (e.g. yyyy/MM/ddTHH:mm:ss.SSSZ).
    const slashIso = text.replace(
      /^(\d{4})\/(\d{1,2})\/(\d{1,2})T/,
      (_, y, m, d) => `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}T`
    )
    if (slashIso !== text) {
      const slashIsoParsed = parseISO(slashIso)
      if (isValid(slashIsoParsed)) {
        return slashIsoParsed
      }
    }
  }
  const parsed = new Date(value)
  return isValid(parsed) ? parsed : null
}

/**
 * データ種別行のメタ（中央列）表示: ステータス変更日時 + 変更者名（IF02095 status_meta_by_data_kind 由来、SR-007 拡張）。 モックアップ（mapping_v2/shared-utils.js
 * generateDataTypeTable）と同じ「変更者 + 変更日時（薄色）」レイアウト用に、ユーザー名と整形済み日時を分離して返す。 更新日時・変更者のいずれかが空のときは
 * null（更新ラベルを表示せず、呼び出し側で「—」フォールバック）。
 *
 * @param {Record<string, { updateUserName?: string; updateDateTime?: string }> | null | undefined} statusMetaByDataKind
 * @param {string | null | undefined} kindCode
 * @returns {{ user: string; date: string } | null}
 */
export const formatGenericKindStatusMeta = (statusMetaByDataKind, kindCode) => {
  const code = String(kindCode ?? '').trim()
  if (!code) return null
  const meta = statusMetaByDataKind?.[code]
  if (!meta) return null
  const date = formatTaskScreenDateTime(meta.updateDateTime ?? '')
  const user = String(meta.updateUserName ?? '').trim()
  // 更新日時・変更者の両方がそろっているときだけ更新ラベルを表示する。
  // どちらか一方でも欠けていれば null を返す（片方だけ表示されるのを防ぐ）。
  if (date === '' || user === '') return null
  return { user, date }
}

/**
 * マッピング行のメタ（中央列）: 該当データ種の upload_file_list 行のうち最新 update_date_time を表示（last_update_user_name は API 拡張で返るが本 UI は日時のみ使用）。
 *
 * @param {import('./types').UploadFileListItem[] | null | undefined} uploadFileList
 * @param {string | null | undefined} kindCode
 * @returns {string}
 */
export const formatGenericKindRowMeta = (uploadFileList, kindCode) => {
  const code = String(kindCode ?? '').trim()
  if (!code) return ''
  const rows = (uploadFileList ?? []).filter((r) => String(r?.dataClassification ?? '').trim() === code)
  if (rows.length === 0) return ''
  let best = rows[0]
  let bestMs = new Date(best?.updateDateTime ?? 0).getTime()
  if (Number.isNaN(bestMs)) bestMs = 0
  for (let i = 1; i < rows.length; i += 1) {
    const r = rows[i]
    const ms = new Date(r?.updateDateTime ?? 0).getTime()
    if (!Number.isNaN(ms) && ms >= bestMs) {
      best = r
      bestMs = ms
    }
  }
  return formatTaskScreenDateTime(best?.updateDateTime)
}

/**
 * 財務テーブル「対象」列: Mode コードまたは表示ラベルを画面用に正規化（SR-011, SR-098） API が standalone/consolidated または既に「単体」「連結」を返すいずれも許容する。
 *
 * @param {string | null | undefined} target
 * @returns {string}
 */
export const formatAccountingTarget = (target) => normalizeAccountingTargetForDisplay(target)

/**
 * Generic_data_kinds をデータ超種でグループ化する（返却順はリスト先頭からの出現順）。
 *
 * @param {import('./types').GenericDataKindItem[] | null | undefined} genericDataKinds
 * @returns {{
 *   groupKey: string
 *   superKindCode: string
 *   superKindName: string
 *   kinds: import('./types').GenericDataKindItem[]
 * }[]}
 */
export const groupGenericDataKindsBySuperKind = (genericDataKinds) => {
  const list = genericDataKinds ?? []
  const order = []
  /**
   * @type {Map<
   *   string,
   *   {
   *     groupKey: string
   *     superKindCode: string
   *     superKindName: string
   *     kinds: import('./types').GenericDataKindItem[]
   *   }
   * >}
   */
  const map = new Map()
  for (const k of list) {
    const superCode = String(k.genericDataSuperKindCode ?? '').trim()
    const superName = String(k.genericDataSuperKindName ?? '').trim()
    const kindCode = String(k.genericDataKindCode ?? '').trim()
    const groupKey = superCode || (superName ? `name:${superName}` : kindCode ? `kind:${kindCode}` : 'kind:__empty__')
    if (!map.has(groupKey)) {
      map.set(groupKey, {
        groupKey,
        superKindCode: superCode,
        superKindName: superName || superCode || kindCode,
        kinds: []
      })
      order.push(groupKey)
    }
    map.get(groupKey).kinds.push(k)
  }
  return order.map((key) => map.get(key))
}

/**
 * 全体進捗: 分母・分子・率・ステータス別件数（SR-008, SR-027 凡例・セグメント用） status が未設定のデータ分類は unset としてカウントする。
 *
 * 既定では汎用項目（GDK-0030）は全体サマリーの分母から除外する。データ超種（折りたたみヘッダー）行の進捗だけは `{ excludeGenericItem: false }`
 * で当該グループ内の全データ分類（汎用項目を含む）を分母に含める。
 *
 * @param {import('./types').GenericDataKindItem[] | null | undefined} genericDataKinds
 * @param {Record<string, string> | null | undefined} statusByDataKind
 * @param {{ excludeGenericItem?: boolean }} [options]
 * @returns {{ denominator: number; numerator: number; percent: number; countsByStatus: Record<string, number> }}
 */
export const calcOverallProgress = (genericDataKinds, statusByDataKind, options) => {
  const excludeGenericItem = options?.excludeGenericItem !== false
  const kinds = (genericDataKinds ?? []).filter((k) => {
    if (!excludeGenericItem) return true
    return k?.genericDataKindCode !== GENERIC_DATA_KIND_GENERIC_ITEM
  })
  const byKind = statusByDataKind ?? {}
  const denominator = kinds.length
  /** @type {Record<string, number>} */
  const countsByStatus = {}
  let numerator = 0

  for (const row of kinds) {
    const code = row?.genericDataKindCode
    const status = code == null || code === '' ? 'unset' : (byKind[code] ?? 'unset')
    countsByStatus[status] = (countsByStatus[status] ?? 0) + 1
    if (PROGRESS_NUMERATOR_STATUSES.has(status)) {
      numerator += 1
    }
  }

  const percent = denominator === 0 ? 0 : (numerator / denominator) * 100

  return { denominator, numerator, percent, countsByStatus }
}

/**
 * Generic_data_kinds が 0 件のとき全体進捗サマリーを出さない（SR-009）
 *
 * @param {import('./types').GenericDataKindItem[] | null | undefined} genericDataKinds
 * @returns {boolean}
 */
export const shouldShowProgressSummary = (genericDataKinds) => {
  const list = genericDataKinds ?? []
  return list.length > 0
}

/**
 * GenericStatusManagement の Status からバッジ用ラベル・色キーを返す（SR-017）
 *
 * @param {string | null | undefined} status
 * @returns {{ label: string; badgeColor: string; rawStatus: string }}
 */
export const statusBadgeProps = (status) => {
  const raw = status == null || status === '' ? 'unset' : String(status)
  const canonical = GENERIC_STATUS_KEY_ALIAS[raw] ?? raw
  const mapped = GENERIC_STATUS_BADGE_MAP[canonical]
  if (mapped) {
    return { label: mapped.label, badgeColor: mapped.badgeColor, rawStatus: raw }
  }
  return { label: raw, badgeColor: 'gray', rawStatus: raw }
}

/**
 * GenericUploadFile.UploadFileStatusEnum（IF02095 upload_file_list[].status）→ 一覧のチップ表示（SR-099）
 *
 * @param {string | null | undefined} status
 * @returns {{ label: string; badgeColor: string; rawStatus: string }}
 */
export const uploadFileListStatusBadgeProps = (status) => {
  const raw = status == null ? '' : String(status).trim()
  /** @type {Record<string, { label: string; badgeColor: string }>} */
  const map = {
    'pending-virus-check': { label: 'ウイルスチェック待ち', badgeColor: 'yellow' },
    'virus-checking': { label: 'ウイルスチェック中', badgeColor: 'orange' },
    'pending-conversion': { label: '変換待ち', badgeColor: 'blue' },
    uploaded: { label: 'アップロード済', badgeColor: 'green' },
    'pending-deletion': { label: '削除待ち', badgeColor: 'gray' },
    'virus-check-error': { label: 'ウイルスチェックエラー', badgeColor: 'red' },
    'conversion-error': { label: '変換エラー', badgeColor: 'red' },
    'validation-error': { label: '検証エラー', badgeColor: 'red' },
    'unexpected-error': { label: '予期せぬエラー', badgeColor: 'red' }
  }
  if (raw === '') {
    return { label: 'アップロード済', badgeColor: 'green', rawStatus: raw }
  }
  const mapped = map[raw]
  if (mapped) {
    return { label: mapped.label, badgeColor: mapped.badgeColor, rawStatus: raw }
  }
  return { label: raw, badgeColor: 'gray', rawStatus: raw }
}

/**
 * 財務行で「アップロード」リンクを出してよいか（processing 以外）（SR-018 / §7.8.3）
 *
 * @param {string | null | undefined} rowStatus AccountingUploadTask.status
 * @returns {boolean}
 */
export const isFinancialRowUploadable = (rowStatus) => {
  if (rowStatus == null || rowStatus === '') {
    return true
  }
  return rowStatus !== 'processing'
}

/**
 * 財務行でエラー UI（モーダル起動ボタン等）の対象か（SR-018 / §7.9.3）
 *
 * @param {string | null | undefined} rowStatus
 * @returns {boolean}
 */
export const isFinancialRowAccountingError = (rowStatus) => rowStatus === 'error'

/**
 * 親法人でない場合は単体（standalone）の財務行のみ残す（AnalysisData.legacy.js の isParent フィルタに合わせる）
 *
 * @param {import('./types').AccountingUploadTaskItem[] | null | undefined} tasks
 * @param {boolean} isParent
 * @returns {import('./types').AccountingUploadTaskItem[]}
 */
export const filterAccountingUploadTasksForParentContext = (tasks, isParent) => {
  const list = tasks ?? []
  if (isParent) {
    return list
  }
  return list.filter((row) => {
    const t = normalizeAccountingTargetForDisplay(row.target != null ? String(row.target) : '')
    return t === ACCOUNTING_TARGET_STANDALONE_LABEL
  })
}

/**
 * 仕様で定義済みの GenericStatus の一覧（検証・分類用、SR-017）
 *
 * @returns {readonly string[]}
 */
export const listKnownGenericStatuses = () => Object.keys(GENERIC_STATUS_BADGE_MAP)

const parseFlexibleDatetime = (value) => {
  const iso = parseISO(value)
  if (isValid(iso)) return iso
  const slash = parse(value, 'yyyy/MM/dd HH:mm:ss', new Date())
  if (isValid(slash)) return slash
  const dashSpace = parse(value, 'yyyy-MM-dd HH:mm:ss', new Date())
  if (isValid(dashSpace)) return dashSpace
  return null
}

export const formatOperationDuration = (startDatetime, endDatetime) => {
  if (!startDatetime || !endDatetime) return ''
  const start = parseFlexibleDatetime(startDatetime)
  const end = parseFlexibleDatetime(endDatetime)
  if (!start || !end) return ''
  const diffMs = end.getTime() - start.getTime()
  if (diffMs < 0) return ''
  const diffSec = Math.floor(diffMs / 1000)
  if (diffSec < 60) return `${diffSec}秒`
  return `${Math.floor(diffSec / 60)}分${diffSec % 60}秒`
}

/**
 * 期間名から現在の四半期ラベル（`1Q`〜`4Q`）を取り出す。
 *
 * BE の `extract_quarter_label`（chrofy_common/datetime_util.py）と同じ判定にそろえる。
 *
 * - 四半期タスク以外（月次・年次）は null
 * - `YYYY年M月期XQ` 形式でない場合も null
 *
 * @param {string | null | undefined} periodType - IF02095 の period_type
 * @param {string | null | undefined} periodLabel - IF02095 の period_label
 * @returns {string | null}
 */
export const extractQuarterLabel = (periodType, periodLabel) => {
  if (String(periodType ?? '') !== PERIOD_TYPE_QUARTER) return null
  const matched = /^(\d{4})年(\d{1,2})月期(\d)Q$/.exec(String(periodLabel ?? '').trim())
  return matched ? `${matched[3]}Q` : null
}
